import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import dayjs from 'dayjs';
import { isNonNullish } from 'remeda';
import { CurrencyCodeSchema } from 'types/currency';
import { z } from 'zod/v4';

import { type ApiError, addResourceNameToQueryResult, useAxiosInstance } from './utils';

const PaymentPlanResourcesVariablesSchema = z.object({
  debtorId: z.string().nullable(),
});

const PaymentSchema = z.object({
  id: z.coerce.string(),
  reference: z.string(),
  receivedAt: z.string().transform(dayjs),
  amounts: z.object({
    debtorCurrency: z.object({
      currency: CurrencyCodeSchema,
      remainingBalance: z.coerce.number(),
      total: z.coerce.number(),
    }),
    originalCurrency: z.object({
      currency: CurrencyCodeSchema,
      remainingBalance: z.coerce.number(),
      total: z.coerce.number(),
    }),
  }),
});

const PaymentPlanResourcesSchema = z.object({
  accountingPayments: z.array(PaymentSchema),
  thirdPartyPayments: z.array(PaymentSchema),
  creditNotes: z.array(PaymentSchema),
  invoices: z.array(
    z.object({
      id: z.coerce.string(),
      reference: z.string(),
      dueDate: z.string().transform(dayjs),
      amounts: z.object({
        debtorCurrency: z.object({
          currency: CurrencyCodeSchema,
          remainingBalance: z.coerce.number(),
          total: z.coerce.number(),
        }),
        originalCurrency: z.object({
          currency: CurrencyCodeSchema,
          remainingBalance: z.coerce.number(),
          total: z.coerce.number(),
        }),
      }),
    }),
  ),
});

export type PaymentPlanResourcesVariables = z.infer<typeof PaymentPlanResourcesVariablesSchema>;
export type PaymentPlanResources = z.infer<typeof PaymentPlanResourcesSchema>;

export async function loadPaymentPlanResourcesFn(
  axiosInstance: Promise<AxiosInstance>,
  { queryKey }: any,
) {
  const instance = await axiosInstance;
  const { data } = await instance.get(`debtors/${queryKey[1].debtorId}/payment-plan-resources`);

  return PaymentPlanResourcesSchema.parse(data);
}

export function useLoadPaymentPlanResources(vars: PaymentPlanResourcesVariables) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<PaymentPlanResources, ApiError>({
    queryKey: ['payment-plan-resources', vars],
    queryFn: context => loadPaymentPlanResourcesFn(axiosInstance, context),
    enabled: isNonNullish(vars.debtorId),
  });

  return addResourceNameToQueryResult<PaymentPlanResources, ApiError, 'paymentPlanResources'>(
    'paymentPlanResources',
    queryResult,
  );
}
